import { Box, Text, useBreakpointValue } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { QueryFilters } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { getShortNormalisationLabel } from '@resistapp/common/normalisation-mode';
import { NormalisationMode } from '@resistapp/common/types';
import { type FormatOptionLabelMeta } from 'react-select';
import { FilterBarResistomapSelect as ResistomapSelect } from '../../forms/filter-bar-resistomap-select';
import { theme } from '../../shared/theme';

export type Props = { queryFilters: QueryFilters; compact?: boolean };

type NormalisationOption = {
  label: string;
  primaryLabel: string;
  secondaryLabel?: string;
  selectedLabel?: string;
  value: NormalisationMode;
};

export function NormalisationModeSelector({ queryFilters, compact = false }: Props) {
  const { availableNormalisationModes } = useSampleDataContext();
  const screenSize = useBreakpointValue({ base: 'base', md: 'md', lg: 'lg', lg2: 'lg2', xl: 'xl' });
  const isWideScreen = screenSize === 'xl' || screenSize === 'lg2' || screenSize === 'md';
  const isWide = compact ? false : isWideScreen;

  // Create options for the dropdown
  const options = availableNormalisationModes.map(mode => buildNormalisationOption(mode, isWide));

  // Find the currently selected option
  const selectedOption = options.find(option => option.value === queryFilters.filters.normalisationMode) || options[0];

  return (
    <Container>
      <ResistomapSelect<NormalisationOption, false>
        options={options}
        value={selectedOption}
        onChange={option => {
          if (option?.value) {
            queryFilters.setNormalisationModeStable(option.value);
          }
        }}
        formatOptionLabel={renderNormalisationOption}
        chakraStyles={{
          valueContainer: provided => ({
            ...provided,
            paddingRight: theme.spacing[1],
          }),
          singleValue: provided => ({
            ...provided,
            marginRight: 0,
          }),
          indicatorsContainer: provided => ({
            ...provided,
            paddingRight: theme.spacing[1],
          }),
          dropdownIndicator: provided => ({
            ...provided,
            paddingLeft: theme.spacing[1],
            paddingRight: theme.spacing[1],
          }),
        }}
        size="md"
      />
    </Container>
  );
}

function buildNormalisationOption(mode: NormalisationMode, isWide: boolean): NormalisationOption {
  const label = getShortNormalisationLabel(mode, isWide);

  switch (mode) {
    case NormalisationMode.LITRE_16S:
      return {
        label,
        primaryLabel: isWide ? 'Copies / L' : '/ L',
        secondaryLabel: '16S std. curve',
        selectedLabel: isWide ? 'Copies / L (16S)' : label,
        value: mode,
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA_16S:
      return {
        label,
        primaryLabel: isWide ? 'Copies / 10 uL DNA' : '/ 10 uL',
        secondaryLabel: '16S std. curve',
        selectedLabel: isWide ? 'Copies / 10 uL DNA (16S)' : label,
        value: mode,
      };
    case NormalisationMode.LITRE_AY:
      return {
        label,
        primaryLabel: isWide ? 'Copies / L' : '/ L',
        secondaryLabel: 'AY std. curve',
        selectedLabel: isWide ? 'Copies / L (AY)' : label,
        value: mode,
      };
    case NormalisationMode.TEN_UL_DILUTED_DNA_AY:
      return {
        label,
        primaryLabel: isWide ? 'Copies / 10 uL DNA' : '/ 10 uL',
        secondaryLabel: 'AY std. curve',
        selectedLabel: isWide ? 'Copies / 10 uL DNA (AY)' : label,
        value: mode,
      };
    default:
      return {
        label,
        primaryLabel: label,
        value: mode,
      };
  }
}

function renderNormalisationOption(option: NormalisationOption, meta: FormatOptionLabelMeta<NormalisationOption>) {
  if (meta.context === 'value' || !option.secondaryLabel) {
    return option.selectedLabel ?? option.label;
  }

  return (
    <Box lineHeight="1.1" whiteSpace="normal">
      <Text as="div" fontSize={theme.fontSize.medium} lineHeight="18px">
        {option.primaryLabel}
      </Text>
      {option.secondaryLabel ? (
        <Text as="div" fontSize={theme.fontSize.small} lineHeight="14px" color="inherit" opacity={0.72}>
          {option.secondaryLabel}
        </Text>
      ) : null}
    </Box>
  );
}

const Container = styled.div`
  width: 100%;
`;
